// Finding a pod, answered from where the operator is standing.
//
// The search looks at this station first: an operator asking about a pod wants
// to know its state *here* — whether it is ready to work on, or still waiting
// upstream. Only a pod with nothing open at this station comes back from
// somewhere else, and those rows say so, because opening one moves the
// station.
//
// Every pod is searchable, whatever state it is in — a pod that is finished,
// cancelled or still a draft is still a pod someone can be holding a label for.
// State orders the results instead: in progress first, then to close, done,
// draft, cancelled.

import { useEffect, useState } from 'react';
import { Sheet } from '@/components/ui/Sheet';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { QueueRow } from '@/components/pod/PodParts';
import type { PodVM } from '@/api/types';
import { useT } from '@/i18n/useT';
import { usePodSearch } from '@/queries';
import { useStore } from '@/store';

/** Wait for a pause in typing before asking the server. */
function useDebounced(value: string, ms = 300): string {
  const [held, setHeld] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setHeld(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return held;
}

export function PodFinderSheet({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (pod: PodVM) => void;
}) {
  const t = useT();
  const workcentreId = useStore((s) => s.workcentreId);
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query);
  const results = usePodSearch(debounced);

  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const pods = results.data ?? [];
  const typed = debounced.trim().length >= 2;

  return (
    <Sheet open={open} onClose={onClose} variant="tall" title={t.findPod}>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t.findPodHint}
        aria-label={t.findPodHint}
        // Opened for one reason: to type in it. Sheet focuses this on open.
        data-autofocus="true"
        autoComplete="off"
        // A wedge scan here is a scan, not typing — see useHardwareScanner.
        data-scan-passthrough="true"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          width: '100%',
          height: 46,
          borderRadius: 12,
          background: 'var(--surface2)',
          border: '1.5px solid var(--line)',
          padding: '0 14px',
          font: '500 14px Manrope,sans-serif',
          color: 'var(--ink)',
          marginBottom: 12,
        }}
      />

      {!typed ? (
        <EmptyState>{t.findPodHint}</EmptyState>
      ) : results.isPending ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <Skeleton height={54} />
          <Skeleton height={54} />
        </div>
      ) : results.isError ? (
        <EmptyState>{(results.error as Error).message}</EmptyState>
      ) : pods.length === 0 ? (
        <EmptyState>
          No pod matches “{debounced.trim()}”.
        </EmptyState>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {pods.map((p) => (
            <div key={p.woId}>
              <QueueRow pod={p} active={false} onClick={() => onPick(p)} />
              {p.workcentreId !== workcentreId && (
                <p
                  style={{
                    margin: '3px 0 0 12px',
                    font: '500 10.5px Manrope,sans-serif',
                    color: 'var(--sub)',
                  }}
                >
                  Not at your station — it is at {p.workcentreName}. Opening it
                  moves you there.
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </Sheet>
  );
}
